import { useCallback, useEffect, useRef, useState } from "react";
import lottoApi from "./api";
import { useAuth } from "../contexts/JWTContext";

/*
 * แรงค์สมาชิก — ตรรกะฝั่งลูกค้า (ใช้ร่วมกันทั้ง landing และ landing-ver2; ไฟล์นี้เหมือนกันทั้งสองแอป)
 * หน้าตาแยกกันที่ pages/Ranking.js (landing = เต็นท์ละครสัตว์ · landing-ver2 = นีออน)
 *   • เซิร์ฟเวอร์ตัดสินทุกอย่าง (เลื่อนขั้น/ลำดับการรับ/เทิร์น/ขอบเขตหวย/เปิด-ปิดระบบ) — ฝั่งนี้แค่แสดงผลและส่งคำสั่งกดรับ
 *   • เปิดหน้า = เซิร์ฟเวอร์สร้างรายการรอรับที่ยังไม่ถูกสร้างให้ก่อนตอบ
 */

const nf = (v) => Number(v || 0).toLocaleString("th-TH", { maximumFractionDigits: 2 });
export const fmtBaht = (v) => `฿${nf(v)}`;

/** path จากเซิร์ฟเวอร์ (/rank-assets/… หรือ /uploads/…) → URL เต็ม */
export const rankImg = (url) => {
  const u = String(url || "");
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  return `${lottoApi.base || ""}${u.startsWith("/") ? u : `/${u}`}`;
};

/** ป้ายสถานะของแต่ละขั้นในบันได */
export const STATE_LABEL = {
  DONE: "ผ่านแล้ว",
  CURRENT: "แรงค์ของคุณ",
  PENDING: "รอรับโบนัส",
  LOCKED: "ยังไม่ถึง",
};

/** โบนัสของขั้นนี้ให้ข้อความสั้น ๆ: "โบนัส ฿50 · เทิร์น 5 เท่า" */
export function bonusText(r) {
  if (!r || !(Number(r.bonus) > 0)) return "";
  return `โบนัส ${fmtBaht(r.bonus)}${r.turnover_multiplier > 0 ? ` · เทิร์น ${nf(r.turnover_multiplier)} เท่า` : " · ไม่ติดเทิร์น"}`;
}

/** โบนัสนี้แทงได้ที่ไหน — null = แทงได้ทุกหวย (ไม่ต้องแสดง) */
export const scopeText = (r) => (r && r.play_scope && r.play_scope !== "ALL" && r.play_label ? `แทงได้${r.play_label}` : null);

/** ข้อความเป้าหมายถัดไป */
export function goalText(view) {
  if (!view || !view.next) return "คุณอยู่แรงค์สูงสุดแล้ว";
  return `ฝากสะสมอีก ${fmtBaht(view.remain)} เพื่อเลื่อนเป็น ${view.next.name}`;
}

export const turnPct = (a) => (a && a.turnover_required > 0 ? Math.max(0, Math.min(100, (a.turnover_done / a.turnover_required) * 100)) : 0);

export { whenText } from "./creditCodeLogic";

const EMPTY = { enabled: true, ladder: [], pending: [], history: [], active: [], current: null, next: null, deposit: 0, remain: 0, progress_pct: 0 };

export function useRanking(onDone) {
  const { user, isAuthenticated } = useAuth();
  const uid = isAuthenticated ? user?.id : null;
  const [view, setView] = useState(null); // null = ยังโหลดไม่เสร็จ
  const [claimingId, setClaimingId] = useState(null);
  const [result, setResult] = useState(null); // { ok:true, ...claim } | { ok:false, message }
  const alive = useRef(true);
  const busy = useRef(false);
  const cb = useRef(onDone);
  cb.current = onDone;

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const load = useCallback(() => {
    if (!uid) return Promise.resolve();
    return Promise.resolve()
      .then(() => lottoApi.myRanking())
      .then((v) => alive.current && setView({ ...EMPTY, ...(v || {}) }))
      .catch(() => alive.current && setView((prev) => prev || { ...EMPTY, failed: true }));
  }, [uid]);

  useEffect(() => {
    setView(null);
    setResult(null);
    load();
    const onVis = () => !document.hidden && load();
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [load]);

  const claim = useCallback(
    async (p) => {
      if (busy.current || !p) return;
      busy.current = true;
      setClaimingId(p.id);
      try {
        const res = await lottoApi.claimRank(p.id);
        if (alive.current) setResult({ ok: true, ...(res?.data || {}), message: res?.message });
        if (cb.current) cb.current(res?.data);
      } catch (err) {
        const d = err?.response?.data;
        if (alive.current) setResult({ ok: false, code: d?.code || "ERROR", message: d?.message || "รับโบนัสไม่สำเร็จ ลองใหม่อีกครั้ง" });
      } finally {
        busy.current = false;
        if (alive.current) setClaimingId(null);
        load();
      }
    },
    [load],
  );

  return {
    isAuthenticated,
    loading: !!uid && view === null,
    failed: !!(view && view.failed),
    enabled: view ? view.enabled !== false : true,
    view: view || EMPTY,
    claimingId,
    claim,
    result,
    closeResult: () => setResult(null),
    reload: load,
  };
}
